import { Injectable } from '@angular/core';
import { ConfigService } from 'core/config';
import { subjectFromObs } from 'core/rx-js';
import { SSLocalStorageService } from 'core/services';
import jwtDecode from 'jwt-decode';
import { BehaviorSubject, distinctUntilChanged, filter, fromEvent, interval, map, merge, Observable, of, startWith, switchMap, take, tap } from 'rxjs';

export type LRole = 'ROLE_process_creator' | 'ROLE_signer' | 'ROLE_administrator' | 'ROLE_api';


@Injectable({
  providedIn: 'root'
})
export class JwtService {

  readonly jwtSubject = new BehaviorSubject<string>(undefined);
  readonly refreshTokenExistsSubject = new BehaviorSubject<boolean>(undefined);
  get refreshTokenExists() {
    return this.refreshTokenExistsSubject.value;
  }

  #jwtDecodedTimestamp: number;
  readonly jwtDecodedSubject = subjectFromObs({
    observable: this.jwtSubject.pipe(
      filter(Boolean),
      tap(() => {
        this.#jwtDecodedTimestamp = new Date().getTime();
      }),
      map((jwt) => jwtDecode<{ exp: number, sub: string, roles: LRole[] }>(jwt))
    ),
    initialValue: null
  })


  readonly expSubject = subjectFromObs({
    initialValue: null,
    observable: this.jwtDecodedSubject.pipe(
      filter(Boolean),
      map(({ exp }) => exp * 1000),
      distinctUntilChanged()
    )
  })


  readonly #preExpire = (offsetTime: number) => (obs: Observable<number>) =>
    obs.pipe(
      switchMap((exp) => {
        const currentTime = new Date().getTime();

        const expiryTime = exp - offsetTime - currentTime;
        if (expiryTime < 0) {
          return of(true);
        }

        return interval(expiryTime).pipe(
          take(1),
          map(() => true),
          startWith(false)
        )
      })
    )

  readonly aboutToExpireSubject = subjectFromObs({
    observable: this.expSubject.pipe(
      this.#preExpire(10 * 1000)
    ),
    initialValue: false,
  })

  readonly jwtExpiredSubject = subjectFromObs({
    observable: this.expSubject.pipe(
      this.#preExpire(5 * 1000)
    ),
    initialValue: false,
  })


  readonly userInactiveSubject = subjectFromObs({
    initialValue: false,
    observable: of(null)
  })


  readonly userInteractionEvents = [
    'mousemove',
    'mousedown',
    'keypress',
    'scroll',
    'touchstart'
  ];

  get #inactiveFrom() {
    return this._localStorageService.get('inactiveFrom');
  }

  get #sessionTimeout() {
    return this._configService.appConfig.sessionTimeout * 60 * 1000
  }

  readonly activityOccuredSubject = subjectFromObs({
    initialValue: undefined,
    observable: this.jwtDecodedSubject.pipe(
      map(Boolean),
      distinctUntilChanged(),
      switchMap((jwtExists) => {
        if (!jwtExists) {
          return of(true);
        }

        return interval(this.#sessionTimeout).pipe(
          map(() => {
            const currentTime = new Date().getTime();
            return (currentTime - this.#inactiveFrom) <= this.#sessionTimeout;
          })
        )
      }
      ),
      distinctUntilChanged()
    )
  })


  readonly userLastActivityObs = merge(
    ...this.userInteractionEvents.map(eventName => {
      return fromEvent(document, eventName)
    })
  )


  constructor(
    private readonly _localStorageService: SSLocalStorageService,
    private readonly _configService: ConfigService

  ) {

    this.userLastActivityObs.subscribe(() => {
      this._localStorageService.set('inactiveFrom', new Date().getTime())
    })
  }


  reset() {

    this.refreshTokenExistsSubject.next(false);
    this.jwtSubject.next(undefined);
  }



}
